import { useI18N, useResponsive, useTheme } from "@sundae/react-hooks";
import { AssetAmount } from "@sundaeswap/asset";
import {
  ArcElement,
  ChartData,
  Chart as ChartJS,
  ChartTypeRegistry,
  Legend,
  Tooltip,
} from "chart.js";
import divide from "lodash/divide";
import { FC, RefObject, useMemo, useRef } from "react";
import { Doughnut } from "react-chartjs-2";
import { DEFAULT_DECIMALS } from "../../../constants/cardano.constants";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { IAssetMetaData } from "../../../types/Asset.types";
import { getAssetName } from "../../../utils/assets.utils";
import {
  createRgbaGradient,
  getChartLegendFontConfig,
  highlightDoughnutLegendItemOnHover,
} from "../../../utils/charts.utils";
import { numberI18n, percentageI18n } from "../../../utils/number-format";

const {
  theme: { colors, fontFamily },
} = require("@sundae/tailwind-config");
ChartJS.register(ArcElement, Tooltip, Legend);

const defaultPrimaryColor = colors.primary["DEFAULT"];
const defaultSecondaryColor = colors.secondary["DEFAULT"];

const TasteTestTokenomicsPieChart: FC = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const {
    state: { tasteTest, tasteTestPersonalization },
  } = useTasteTestContext();
  const responsive = useResponsive();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const chartRef = useRef<ChartJS<keyof ChartTypeRegistry> | null>(null);
  const primaryChartColor = tasteTestPersonalization?.primaryColor || defaultPrimaryColor;
  const secondaryChartColor = tasteTestPersonalization?.secondaryColor || defaultSecondaryColor;

  const labels = tasteTest?.projectDetails.tokenomics.sort().map((item) => item.label);
  const sortedTokenomics = tasteTest?.projectDetails.tokenomics.sort() ?? [];
  const backgroundColors = useMemo(
    () =>
      createRgbaGradient(
        secondaryChartColor,
        primaryChartColor,
        tasteTest?.projectDetails.tokenomics.length,
        isDark ? 0.8 : 0.7
      ),
    [tasteTest?.projectDetails.tokenomics.length, isDark]
  );
  const hoverBackgroundColors = createRgbaGradient(
    secondaryChartColor,
    primaryChartColor,
    tasteTest?.projectDetails.tokenomics.length,
    isDark ? 0.2 : 0.4
  );
  const borderColors = useMemo(
    () =>
      createRgbaGradient(
        secondaryChartColor,
        primaryChartColor,
        tasteTest?.projectDetails.tokenomics.length
      ),
    [tasteTest?.projectDetails.tokenomics.length]
  );

  const data = useMemo(() => {
    return {
      labels,
      datasets: [
        {
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
          data: sortedTokenomics.map((item) => item.percentage),
          hoverBackgroundColor: hoverBackgroundColors,
          label: "% of Total Supply",
          offset: sortedTokenomics.map(({ label }) => (label.includes("Taste Test") ? 55 : 10)), // Explode the third segment
        },
      ],
    } as ChartData<"doughnut">;
  }, [borderColors, backgroundColors, labels, sortedTokenomics]);

  return (
    <Doughnut
      data={data}
      ref={chartRef as RefObject<ChartJS<"doughnut">>}
      options={{
        animation: true,
        responsive: true,
        offset: 20,
        onHover: (_event, elements) => highlightDoughnutLegendItemOnHover(chartRef, elements, data),
        plugins: {
          tooltip: {
            callbacks: {
              label: (tooltipItem) => {
                const normalizedPercentage = divide(tooltipItem.parsed, 100);
                const assetAmount = new AssetAmount(tasteTest?.asset.totalSupply, tasteTest?.asset);
                const absoluteShare = assetAmount.value.toNumber() * normalizedPercentage;
                return `${t("detail.projectDetails.tokenomics.tooltip")}: ${percentageI18n(
                  normalizedPercentage,
                  currentLocale
                )} (${numberI18n(
                  absoluteShare,
                  { maximumFractionDigits: tasteTest?.asset.decimals ?? DEFAULT_DECIMALS },
                  currentLocale
                )} ${getAssetName(
                  (tasteTest?.asset as unknown as IAssetMetaData) ?? ({} as IAssetMetaData)
                )})`;
              },
            },
          },
          legend: {
            labels: {
              ...getChartLegendFontConfig(isDark, responsive),
              padding: 16,
              boxWidth: 8,
              boxHeight: 8,
            },
            position: responsive.lg ? "bottom" : "top",
            title: {
              padding: {
                top: 32,
              },
              display: true,
              text: t("detail.projectDetails.tokenomics.title"),
              color: isDark ? colors.gray[200] : colors.black,
              font: {
                family: fontFamily.sans[0],
                weight: "normal",
                size: 13.5,
              },
            },
          },
        },
      }}
    />
  );
};

export default TasteTestTokenomicsPieChart;
